/**
 * Opening-sketch "distance to element" controller.
 *
 * Owns a radial menu that appears when a single OPENING_SKETCH is selected in
 * the OpeningSketchDrawTool, plus the pick-target + numeric-input flow behind
 * its one op: "measure distance to another element".
 *
 * Flow:
 *   1. Tool calls `openMenuForSelection(worldAnchor)` on select.
 *   2. User clicks the "distance" bubble → enters PICK mode.
 *   3. User hovers/clicks a wall CENTERLINE or a NODE (of any wall sketch on the
 *      floor) as the target. The opening's own edge is excluded.
 *   4. Menu morphs to a number input seeded with the current SHORTEST distance
 *      from the opening span to the target.
 *   5. On confirm, the opening slides ALONG its wall so that shortest distance
 *      equals the typed value (nearest reachable offset), pushing an undo
 *      snapshot and re-cutting the wall hole.
 *
 * Mirrors the LineTool's `EdgeOpController`, but the source is an opening that
 * can only slide along its wall (1-D), and the distance metric is always the
 * shortest point-to-segment distance (never the infinite line).
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { PointoramaWallSketch } from '../../../../utils/PointoramaWallSketch';
import { PixelHitTester } from '../selection/PixelHitTester';
import { ToolUndoRedoManager, Snapshot } from '../../ToolUndoRedoManager';
import { RadialMenu, RadialMenuItem } from '../radialMenu/RadialMenu';
import { EdgeHighlight } from '../line-detection/EdgeHighlight';
import { getSnapStepDisplay, metersToDisplay, displayToMeters } from '../line-detection/distanceUnits';
import { getOpeningSketchOffsetProperty, getOpeningSketchLengthProperty, getOpeningSketchWallEdgeIdProperty } from '@pointorama/potree-shared';
import { openingClearSpanOnEdge } from '../openingSketch/OpeningSketchShared';
import { distanceAtOffset, solveOffsetForDistance, targetAlongCoord, pointAtAlongCoord, type OpeningOnEdge, type DistanceTarget, type Pt } from './openingDistanceMath';
import { edgeLength as arcEdgeLength, edgeIsCurved } from '../../../../utils/skeleton/arcGeometry';

/** Distance-op icon (a double-ended dimension arrow), HeroIcons-24 outline style. */
const ICON_DISTANCE_SVG = `<svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
  <path stroke-linecap="round" stroke-linejoin="round" d="M4 7v10M20 7v10M4 12h16M6.5 9.5 4 12l2.5 2.5M17.5 9.5 20 12l-2.5 2.5" />
</svg>`;

/** Pixel radius for picking a wall centerline / node as the target. */
const CENTERLINE_PICK_RADIUS_PX = 10;
const NODE_PICK_RADIUS_PX = 12;

/** A resolved distance target: its geometry + a stable id + a display anchor. */
interface Target {
  id: string;
  /** Target geometry in the floor plane (segment; p0===p1 for a node). */
  geom: DistanceTarget;
  /** World anchor for the relationship connector (segment midpoint / node). */
  anchor: THREE.Vector3;
}

type Phase = { kind: 'idle' } | { kind: 'menu' } | { kind: 'pick' } | { kind: 'input'; target: Target };

export interface OpeningDistanceControllerDeps {
  readonly viewer: Viewer;
  readonly hitTester: PixelHitTester;
  readonly undoRedo: () => ToolUndoRedoManager | null;
  /** The wall sketch owning the current floor (for edges/nodes + floor Z). */
  readonly getWallSketch: () => PointoramaWallSketch | null;
  /** World endpoints of an opening's footprint span on its wall. */
  readonly openingFootprint: (skeleton: PointoramaWallSketch, openingId: string) => { a: THREE.Vector3; b: THREE.Vector3 } | null;
  /** Re-cut the wall hole after a slide (matches the drag-slide's final pass). */
  readonly rebuildWallHole: () => void;
}

export class OpeningDistanceController {
  private phase: Phase = { kind: 'idle' };
  private openingId: string | null = null;

  private menu: RadialMenu | null = null;
  private highlight: EdgeHighlight | null = null;
  private highlightScene: THREE.Scene | null = null;
  private unregisterHighlightScene: (() => void) | null = null;
  private hoverTargetId: string | null = null;

  constructor(private readonly deps: OpeningDistanceControllerDeps) {}

  isActive(): boolean {
    return this.phase.kind !== 'idle';
  }

  /** True during the pick/input phases, when tool clicks must be swallowed
   *  (the menu-bubbles phase is NOT modal — the tool can still be clicked and
   *  the RadialMenu dismisses itself). */
  isModal(): boolean {
    return this.phase.kind === 'pick' || this.phase.kind === 'input';
  }

  /** Open the radial menu for the just-selected opening. */
  openMenuForSelection(openingId: string, worldAnchor: THREE.Vector3): void {
    this.cancel();
    this.openingId = openingId;
    this.phase = { kind: 'menu' };
    const menu = this.ensureMenu();
    const items: RadialMenuItem[] = [
      {
        id: 'distance',
        iconSvg: ICON_DISTANCE_SVG,
        tooltip: this.deps.viewer.translationService.t('Measure distance to a wall or node'),
        onClick: () => this.startPick(),
      },
    ];
    menu.show({ type: 'items', items, onClose: () => this.cancel() }, worldAnchor);
  }

  private startPick(): void {
    this.phase = { kind: 'pick' };
    this.hoverTargetId = null;
    // Suppress the InputHandler's parallel click-selection for the duration of
    // the pick/input flow — otherwise clicking a target would deselect the
    // opening, and the tool's per-frame refresh would tear this menu down.
    this.deps.viewer.inputHandler.selectionDisabled = true;
    // Hide the menu bubbles while picking; a hint keeps the anchor alive.
    this.menu?.show({ type: 'hint', message: this.deps.viewer.translationService.t('Click a wall or node to measure to') }, this.menu.currentAnchor ?? new THREE.Vector3());
    this.deps.viewer.renderer.domElement.style.cursor = 'crosshair';
  }

  /** Forwarded mousedown. Returns true when consumed. */
  onMouseDown(event: MouseEvent): boolean {
    if (this.phase.kind !== 'pick') return false;
    if (event.button !== 0) return false;
    const target = this.pickTarget(event);
    if (!target) {
      this.cancel();
      return true;
    }
    this.phase = { kind: 'input', target };
    this.refreshHighlight(target, true);
    this.openDistanceInput(target);
    return true;
  }

  /** Forwarded mousemove. Highlights the hovered target during a pick. */
  onMouseMove(event: MouseEvent): void {
    if (this.phase.kind !== 'pick') return;
    const target = this.pickTarget(event);
    const id = target?.id ?? null;
    this.deps.viewer.renderer.domElement.style.cursor = id ? 'pointer' : 'crosshair';
    if (id === this.hoverTargetId) return;
    this.hoverTargetId = id;
    this.refreshHighlight(target, false);
  }

  /** Forwarded keydown. Returns true when consumed. */
  onKeyDown(event: KeyboardEvent): boolean {
    if (this.phase.kind === 'idle') return false;
    if (event.key === 'Escape') {
      this.cancel();
      return true;
    }
    return false;
  }

  // ─── Target picking ──────────────────────────────────────────────────

  /**
   * Pick a wall CENTERLINE segment or a NODE under the cursor, across every wall
   * sketch on the floor. Nodes win over centerlines (smaller target). Excludes
   * the opening's own edge + its endpoints so it can't measure to itself.
   */
  private pickTarget(event: MouseEvent): Target | null {
    const skeleton = this.deps.getWallSketch();
    if (!skeleton) return null;
    const mousePx = this.deps.hitTester.mousePx(event);
    const ownEdgeId = this.openingId ? getOpeningSketchWallEdgeIdProperty(this.openingId).value || '' : '';

    // 1. Nodes (across all wall sketches on this floor group).
    let bestNode: { id: string; pos: THREE.Vector3; d: number } | null = null;
    for (const ws of this.deps.viewer.scene.wallSketches) {
      if (ws.groupIdentifier !== skeleton.groupIdentifier) continue;
      for (const node of ws.getNodes()) {
        const px = this.deps.hitTester.projectWorldToPixel(node.position);
        if (!px) continue;
        const d = px.distanceTo(mousePx);
        if (d <= NODE_PICK_RADIUS_PX && (!bestNode || d < bestNode.d)) {
          bestNode = { id: `node:${node.id}`, pos: new THREE.Vector3(node.position.x, node.position.y, node.position.z), d };
        }
      }
    }
    if (bestNode) {
      const p: Pt = { x: bestNode.pos.x, y: bestNode.pos.y };
      return { id: bestNode.id, geom: { p0: p, p1: p }, anchor: bestNode.pos };
    }

    // 2. Wall centerlines.
    let bestEdge: { id: string; a: THREE.Vector3; b: THREE.Vector3; d: number } | null = null;
    for (const ws of this.deps.viewer.scene.wallSketches) {
      if (ws.groupIdentifier !== skeleton.groupIdentifier) continue;
      for (const edge of ws.getEdges()) {
        if (edge.id === ownEdgeId) continue; // never the opening's own wall
        // Curved walls can't be a distance target in v1 — the target model is
        // a straight segment, and measuring to an arc's chord would lie.
        if (edgeIsCurved(edge)) continue;
        const a = ws.getNode(edge.startNodeId);
        const b = ws.getNode(edge.endNodeId);
        if (!a || !b) continue;
        const ap = this.deps.hitTester.projectWorldToPixel(a.position);
        const bp = this.deps.hitTester.projectWorldToPixel(b.position);
        if (!ap || !bp) continue;
        const d = PixelHitTester.pointToSegmentDistancePx(mousePx, ap, bp);
        if (d <= CENTERLINE_PICK_RADIUS_PX && (!bestEdge || d < bestEdge.d)) {
          bestEdge = {
            id: `edge:${edge.id}`,
            a: new THREE.Vector3(a.position.x, a.position.y, a.position.z),
            b: new THREE.Vector3(b.position.x, b.position.y, b.position.z),
            d,
          };
        }
      }
    }
    if (bestEdge) {
      const mid = bestEdge.a.clone().add(bestEdge.b).multiplyScalar(0.5);
      return { id: bestEdge.id, geom: { p0: { x: bestEdge.a.x, y: bestEdge.a.y }, p1: { x: bestEdge.b.x, y: bestEdge.b.y } }, anchor: mid };
    }
    return null;
  }

  // ─── Distance input + slide ──────────────────────────────────────────

  /** The opening's slide model (edge endpoints + span length), or null. */
  private openingOnEdge(skeleton: PointoramaWallSketch): { model: OpeningOnEdge; edgeId: string; edgeLen: number; length: number } | null {
    if (!this.openingId) return null;
    let edgeId = '';
    let length = 0;
    try {
      edgeId = getOpeningSketchWallEdgeIdProperty(this.openingId).value || '';
      length = getOpeningSketchLengthProperty(this.openingId).value ?? 0;
    } catch {
      return null;
    }
    const edge = skeleton.getEdge(edgeId);
    if (!edge) return null;
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) return null;
    const edgeStart: Pt = { x: a.position.x, y: a.position.y };
    const edgeEnd: Pt = { x: b.position.x, y: b.position.y };
    // Along-wall coordinates are arc lengths on curved edges.
    const edgeLen = arcEdgeLength(edgeStart, edgeEnd, edge.bulge);
    return { model: { edgeStart, edgeEnd, length, bulge: edge.bulge }, edgeId, edgeLen, length };
  }

  private openDistanceInput(target: Target): void {
    const skeleton = this.deps.getWallSketch();
    if (!skeleton || !this.openingId) {
      this.cancel();
      return;
    }
    const onEdge = this.openingOnEdge(skeleton);
    if (!onEdge) {
      this.cancel();
      return;
    }
    const currentOffset = this.readOffset();
    const currentDist = distanceAtOffset(onEdge.model, currentOffset, target.geom);

    const unit = this.deps.viewer.lengthUnitDisplay;
    const stepDisplay = getSnapStepDisplay(unit.code);
    this.menu?.show(
      {
        type: 'input',
        label: this.deps.viewer.translationService.t('dist'),
        unit: unit.code,
        step: String(stepDisplay),
        initialValue: metersToDisplay(currentDist, unit).toFixed(2),
        onConfirm: (raw) => {
          const parsed = parseFloat(raw);
          if (!isFinite(parsed) || parsed < 0) {
            this.cancel();
            return;
          }
          this.applyDistance(target, displayToMeters(parsed, unit));
        },
        onCancel: () => this.cancel(),
      },
      target.anchor.clone(),
    );
  }

  /** Slide the opening so its shortest distance to `target` equals `desired`. */
  private applyDistance(target: Target, desired: number): void {
    const skeleton = this.deps.getWallSketch();
    if (!skeleton || !this.openingId) {
      this.cancel();
      return;
    }
    const onEdge = this.openingOnEdge(skeleton);
    if (!onEdge) {
      this.cancel();
      return;
    }
    const before = this.readOffset();
    // Solve against the corner-clear span so the requested distance never
    // pushes the opening into a wall joined at the edge's ends.
    const span = openingClearSpanOnEdge(skeleton, onEdge.edgeId);
    const minOffset = span.start;
    const maxOffset = Math.max(minOffset, span.end - onEdge.length);
    const after = Math.max(minOffset, Math.min(maxOffset, solveOffsetForDistance(onEdge.model, target.geom, desired, before, maxOffset)));
    if (Math.abs(after - before) > 1e-6) {
      this.writeOffset(after);
      this.deps.rebuildWallHole();
      this.deps.undoRedo()?.pushState(this.createMoveSnapshot(this.openingId, before, after));
    }
    this.cancel();
  }

  private readOffset(): number {
    try {
      return getOpeningSketchOffsetProperty(this.openingId ?? '').value ?? 0;
    } catch {
      return 0;
    }
  }

  private writeOffset(offset: number): void {
    try {
      if (this.openingId) getOpeningSketchOffsetProperty(this.openingId).value = offset;
    } catch {
      /* property service not ready */
    }
  }

  /** Undo snapshot for an offset move (toggles before/after + re-cuts the hole). */
  private createMoveSnapshot(openingId: string, before: number, after: number): Snapshot {
    let applyBefore = true;
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        try {
          getOpeningSketchOffsetProperty(openingId).value = applyBefore ? before : after;
          this.deps.rebuildWallHole();
        } catch {
          /* property service not ready */
        }
        applyBefore = !applyBefore;
        return snapshot;
      },
    };
    return snapshot;
  }

  // ─── Highlight ───────────────────────────────────────────────────────

  /**
   * Highlight the target (wall centerline / node) plus the connector that
   * represents the measured distance: a line COLINEAR with the opening's wall,
   * from the opening center to where that axis crosses the target. Falls back to
   * a center→nearest-point hop when the target is parallel (no crossing).
   */
  private refreshHighlight(target: Target | null, locked: boolean): void {
    const highlight = this.ensureHighlight();
    if (!target) {
      highlight.setEdges([]);
      return;
    }
    const z = target.anchor.z;
    // Target centerline segment (a node collapses to a point → tiny segment).
    const segments: { start: THREE.Vector3; end: THREE.Vector3 }[] = [{ start: new THREE.Vector3(target.geom.p0.x, target.geom.p0.y, z), end: new THREE.Vector3(target.geom.p1.x, target.geom.p1.y, z) }];

    const skeleton = this.deps.getWallSketch();
    const onEdge = skeleton ? this.openingOnEdge(skeleton) : null;
    const footprint = skeleton && this.openingId ? this.deps.openingFootprint(skeleton, this.openingId) : null;
    if (onEdge && footprint) {
      const centerZ = footprint.a.z;
      const centerCoord = this.readOffset() + onEdge.length / 2;
      const centerPt: Pt = pointAtAlongCoord(onEdge.model, centerCoord);
      // Colinear connector: opening center → the along-axis point the distance is
      // measured to (crossing for angled/perpendicular walls, nearest-end
      // projection for parallel walls).
      const targetCoord = targetAlongCoord(onEdge.model, target.geom, centerCoord);
      const crossPt = pointAtAlongCoord(onEdge.model, targetCoord);
      segments.push({ start: new THREE.Vector3(centerPt.x, centerPt.y, centerZ), end: new THREE.Vector3(crossPt.x, crossPt.y, z) });
    }
    highlight.setEdges(segments);
    void locked;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────

  private ensureMenu(): RadialMenu {
    if (!this.menu) this.menu = new RadialMenu(this.deps.viewer);
    return this.menu;
  }

  private ensureHighlight(): EdgeHighlight {
    if (!this.highlight) {
      this.highlightScene = new THREE.Scene();
      this.unregisterHighlightScene = this.deps.viewer.registerToolOverlayScene(this.highlightScene);
      this.highlight = new EdgeHighlight(this.highlightScene);
    }
    return this.highlight;
  }

  /** Tear down the current interaction (menu, highlight, pick state). */
  cancel(): void {
    this.phase = { kind: 'idle' };
    this.openingId = null;
    this.hoverTargetId = null;
    this.highlight?.setEdges([]);
    this.menu?.hide();
    // Restore click-selection suppressed during pick/input.
    this.deps.viewer.inputHandler.selectionDisabled = false;
    this.deps.viewer.renderer.domElement.style.cursor = '';
  }

  dispose(): void {
    this.cancel();
    this.highlight?.dispose();
    this.highlight = null;
    this.unregisterHighlightScene?.();
    this.unregisterHighlightScene = null;
    this.highlightScene = null;
    this.menu?.dispose();
    this.menu = null;
  }
}
